import { Component, OnDestroy, OnInit } from '@angular/core';
import { interval, Subscription } from 'rxjs';
import { LeadCard, LeadPopupService } from 'src/app/shared-modules/services/lead-popup.service';

/**
 * New-lead cards (docs/plan-lead-popup.md, LP2) - top right under the top bar, so they never
 * cover the attendance card (bottom right). Newest on top, at most 3, then "+N more".
 * Card words: "lead" (user 2026-11-03); nothing hints that other centres got the same lead.
 */
@Component({
  selector: 'fyt-lead-offer-cards',
  templateUrl: './lead-offer-cards.component.html',
  styleUrls: ['./lead-offer-cards.component.css']
})
export class LeadOfferCardsComponent implements OnInit, OnDestroy {
  static readonly MAX_SHOWN = 3;

  cards: LeadCard[] = [];
  soundOn = true;
  now = Date.now();
  // Offset from the card's top-centre to the top bar's Leads tab, so a new card flies out of it
  fly: { [prop: string]: string } | null = null;
  private readonly subs = new Subscription();

  constructor(public popup: LeadPopupService) { }

  ngOnInit(): void {
    this.subs.add(this.popup.cards$.subscribe(c => {
      if (c.length > this.cards.length) { this.fly = this.flyFrom(); }
      this.cards = c;
      this.now = Date.now();
    }));
    this.subs.add(this.popup.sound$.subscribe(on => this.soundOn = on));
    this.subs.add(interval(30000).subscribe(() => this.now = Date.now()));   // "2 min ago"
  }

  ngOnDestroy(): void { this.subs.unsubscribe(); }

  get shown(): LeadCard[] { return this.cards.slice(-LeadOfferCardsComponent.MAX_SHOWN).reverse(); }
  get extra(): number { return Math.max(0, this.cards.length - LeadOfferCardsComponent.MAX_SHOWN); }

  byId(_: number, c: LeadCard): string { return c.offer.offerId; }

  // Desktop only (the stack sits at the bottom on phones); plain slide-in when the tab is
  // hidden or scrolled out of view. Numbers match .loc-stack (top 72px, right 16px, width 360px).
  private flyFrom(): { [prop: string]: string } | null {
    const r = this.popup.anchorRect();
    if (!r || r.bottom <= 0 || window.innerWidth <= 767) { return null; }
    const width = Math.min(360, window.innerWidth - 32);
    const cardCentreX = window.innerWidth - 16 - width / 2;
    return {
      '--loc-fx': Math.round(r.left + r.width / 2 - cardCentreX) + 'px',
      '--loc-fy': Math.round(r.top + r.height / 2 - 72) + 'px'
    };
  }

  ago(c: LeadCard): string {
    const min = Math.floor((this.now - new Date(c.offer.sentAt).getTime()) / 60000);
    if (!isFinite(min) || min < 1) { return 'just now'; }
    if (min < 60) { return min + ' min ago'; }
    const h = Math.floor(min / 60);
    return h + (h === 1 ? ' hour ago' : ' hours ago');
  }

  chip(c: LeadCard): { text: string; cls: string } {
    switch (c.state) {
      case 'ok': return { text: 'Accepted', cls: 'loc-chip' };
      case 'pay': return { text: 'No credits', cls: 'loc-chip loc-chip-amber' };
      case 'converted': return { text: 'Check first', cls: 'loc-chip loc-chip-amber' };
      case 'closed': return { text: 'Closed', cls: 'loc-chip loc-chip-grey' };
      case 'error': return { text: 'Not accepted', cls: 'loc-chip loc-chip-red' };
      default: return c.offer.reminder ? { text: 'Still waiting', cls: 'loc-chip loc-chip-soft' } : { text: 'New lead', cls: 'loc-chip' };
    }
  }

  where(c: LeadCard): string {
    const km = c.offer.distanceKm !== null && c.offer.distanceKm !== undefined ? c.offer.distanceKm + ' km from ' : 'Near ';
    return km + (c.offer.branchName || 'your centre');
  }

  noCredits(c: LeadCard): boolean { return c.offer.cost === 'credit' && c.offer.creditsLeft <= 0; }

  acceptLabel(c: LeadCard): string {
    if (c.state === 'error') { return 'Try again'; }
    if (c.offer.cost === 'free') { return 'Accept free'; }
    return this.noCredits(c) ? 'Pay & accept' : 'Accept · 1 credit';
  }
}
